import React, { useMemo, useState, useRef } from 'react';
import classnames from 'classnames';
import { useMediaQuery } from '@mui/material';

import { Agent, MappedAgent } from '../../apolloClient/types/Agents';

import FlagLabel from '../FlagLabel';
import Button from '../Button';
import { mapAgent } from '../../src/utils/agentUtils';
import ContactUsDialog from 'components/ContactUs/ContactUsDialog';
import { usePhoneNumber } from '../../hooks/usePhoneNumber';
import Carousel from '../Carousel';
import ExpertCard from './ExpertCard';
import { ExpertsCarouselArrow } from './ExpertsCarouselArrow';

type Props = {
  flagLabelTextSize?: string;
  isHomePage?: boolean;
  experts: Agent[];
  // optional props for customizable ExpertCard image dimensions
  expertCardImageWidth?: string;
  expertCardImageHeight?: string;
};

const Experts: React.FC<Props> = ({
  isHomePage,
  flagLabelTextSize,
  experts,
  expertCardImageWidth,
  expertCardImageHeight,
}) => {
  const [open, setOpen] = useState(false);
  const { dialogAreaCode, dialogPhoneEnding } = usePhoneNumber({ open });
  const isMobile = useMediaQuery('(max-width:767px)');
  const carouselRef = useRef<{
    scrollPrev: () => void;
    scrollNext: () => void;
    emblaApi: any;
  }>(null);

  const handlePopUpClose = () => {
    setOpen(false);
  };

  const mappedExperts: MappedAgent[] = useMemo(() => {
    return experts
      .sort(
        (a, b) =>
          (a.orderOnHomePage || +Infinity) - (b.orderOnHomePage || +Infinity)
      )
      .reduce<MappedAgent[]>((acc, _) => {
        const mappedAgent = mapAgent(_.agentId);
        mappedAgent && acc.push(mappedAgent);
        return acc;
      }, []);
  }, [experts]);

  return (
    <>
      <section
        className={classnames(
          'pt-3 md:pt-[0px] pb-[31px] md:pb-0 md:mb-[54px] xl:mb-[78px] text-m',
          {
            'container-xl !px-[30px] xl:!px-[41px] md:mb-[78px] xl:mb-[116px] lg:pt-[22px]':
              isHomePage,
            container: !isHomePage,
          }
        )}
      >
        <div className="flex flex-col xl:flex-row">
          <div
            className={classnames(
              'flex flex-col xl:w-1/3 md:flex-row xl:flex-col xl:mb-0 mb-[53px]',
              {
                'mb-[39px] md:mb-[60px]': isHomePage,
                'xl:w-[460px]': !isHomePage,
              }
            )}
          >
            <div
              className={classnames(
                'relative flex flex-col justify-center md:w-1/2 xl:w-auto md:mb-10 xl:mb-0',
                { 'md:ml-[13px]': isHomePage }
              )}
            >
              <div className="relative flex flex-col justify-center w-max">
                <FlagLabel name="the experts" textSize={flagLabelTextSize} />
              </div>
            </div>
            <div
              className={classnames(
                'text-14-21-0.3 md:w-1/2 xl:w-auto xl:text-16-26-0.3'
              )}
            >
              <div
                className={classnames(
                  'mt-[22px] md:mt-[27px] xl:pr-10 xl:mt-8',
                  {
                    'md:pl-[9px xl:pr-[55px] xl:pl-[10px]': isHomePage,
                    'xl:pr-[0px] max-w-[418px]': !isHomePage,
                  }
                )}
              >
                Our condo expert agents live and breathe Miami condos and South
                Florida area condos every day. We never stop learning, so that
                we can guide you to the Miami or South Florida condo that suits
                you best. Browser our site full of exclusive insider info at
                your leisure, and reach out anytime. We are here to help.
              </div>
            </div>
          </div>
          <div
            className={classnames(
              'relative mb-[9px] w-full xl:w-2/3 md:mb-[35px] xl:mb-[62px] md:max-w-[706px] mx-auto',
              {
                'max-w-[313px] lg:max-w-[657px] xl:max-w-[842px]': isHomePage,
                'max-w-[335px]': !isHomePage,
              }
            )}
          >
            <ExpertsCarouselArrow
              direction="prev"
              isVisible={true}
              onClick={() => carouselRef.current?.scrollPrev()}
              customHeight={expertCardImageHeight}
            />
            <div className={'relative experts-homepage'}>
              <Carousel
                ref={carouselRef}
                emblaOptions={{
                  duration: 20,
                  slidesToScroll: 1,
                  containScroll: 'trimSnaps',
                  startIndex: 0,
                  loop: true,
                  align: 'start',
                }}
                showArrows={false}
                className="experts-slider"
                slidesPerView={isMobile ? 2 : 4}
              >
                {mappedExperts.map((expert, index) => (
                  <div key={`${expert.firstName}-${expert.lastName}-${index}`}>
                    {expert.image && (
                      <ExpertCard
                        image={expert.image}
                        firstName={expert.firstName}
                        lastName={expert.lastName}
                        job={experts[index].job}
                        width={expertCardImageWidth}
                        height={expertCardImageHeight}
                      />
                    )}
                  </div>
                ))}
              </Carousel>
            </div>
            <ExpertsCarouselArrow
              direction="next"
              isVisible={true}
              onClick={() => carouselRef.current?.scrollNext()}
              customHeight={expertCardImageHeight}
            />
          </div>
        </div>
        <div
          className={classnames(
            'flex items-center justify-center mx-auto md:w-[324px] xl:w-[445px] xl:pl-6',
            { 'xl:pl-[100px] xl:pr-[15px]': !isHomePage }
          )}
        >
          {!isHomePage ? (
            <Button
              classNames="h-[48px] py-[15px] md:pb-0"
              name="contact us"
              onClick={() => setOpen(true)}
              href="#experts"
            />
          ) : (
            <Button
              classNames="h-[48px] py-[15px] md:pb-0"
              name="Work with us"
              href="https://joinblackbookproperties.com/"
            />
          )}
        </div>
        <ContactUsDialog
          open={open}
          onClose={handlePopUpClose}
          dialogPhoneEnding={dialogPhoneEnding}
          dialogAreaCode={dialogAreaCode}
        />
      </section>
    </>
  );
};

export default Experts;
